import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../api/client";
import { Link, useParams } from "react-router-dom";
import PortalSidebar from "../../components/portal/PortalSidebar";
import { 
  Search, ChevronRight, ChevronLeft, Eye, Clock,
  AlertCircle, CheckCircle, XCircle, Plus, X
} from "lucide-react";
import { toast } from "sonner";

function getStatusColor(status) {
  const colors = {
    'ACTIVE': 'bg-emerald-100 text-emerald-700',
    'PENDING': 'bg-amber-100 text-amber-700',
    'DISCHARGED': 'bg-slate-100 text-slate-600',
    'ON_HOLD': 'bg-red-100 text-red-700',
  };
  return colors[status] || 'bg-slate-100 text-slate-600';
}

function getLienStatusIcon(status) {
  if (status === 'SIGNED') return <CheckCircle size={14} className="text-emerald-600" />;
  if (status === 'PENDING') return <Clock size={14} className="text-amber-600" />;
  if (status === 'REJECTED') return <XCircle size={14} className="text-red-600" />;
  return null;
}

export default function PatientList() {
  const [patients, setPatients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 15;
  
  // Modal state for adding patient
  const [showModal, setShowModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newPatient, setNewPatient] = useState({
    first_name: '',
    last_name: '',
    dob: '',
    case_number: '',
    attorney: '',
    treatment_type: '',
    insurance: '',
    notes: '',
  });

  const fetchPatients = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("limit", perPage);
      params.append("offset", (page - 1) * perPage);
      if (statusFilter) params.append("status", statusFilter);
      if (search) params.append("search", search);

      const res = await api.get(`/portal/patients?${params.toString()}`);
      setPatients(res.data.patients || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error("Failed to fetch patients:", err);
      setError("Failed to load patients");
    } finally {
      setLoading(false);
    }
  }, [page, perPage, statusFilter, search]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPatients();
  };

  const handleCreatePatient = async (e) => {
    e.preventDefault();
    if (!newPatient.first_name.trim() || !newPatient.last_name.trim()) {
      toast.error("Please enter patient first and last name");
      return;
    }
    
    try {
      setCreating(true);
      await api.post('/portal/patients', newPatient);
      toast.success("Patient added successfully");
      setShowModal(false);
      setNewPatient({
        first_name: '', last_name: '', dob: '', case_number: '',
        attorney: '', treatment_type: '', insurance: '', notes: '',
      });
      fetchPatients();
    } catch (err) {
      console.error("Failed to create patient:", err);
      toast.error("Failed to add patient");
    } finally {
      setCreating(false);
    }
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <PortalSidebar />

      <main className="flex-1 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Patients</h1>
              <p className="text-slate-500 mt-1">
                {total} total patients
              </p>
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 h-10 px-4 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
              data-testid="add-patient-btn"
            >
              <Plus size={18} />
              Add Patient
            </button>
          </div>

          {/* Search & Filters */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
            <form onSubmit={handleSearch} className="flex gap-4">
              <div className="flex-1 relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by name or case number..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                className="h-10 px-4 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">All Statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="PENDING">Pending</option>
                <option value="DISCHARGED">Discharged</option>
                <option value="ON_HOLD">On Hold</option>
              </select>
              <button
                type="submit"
                className="h-10 px-6 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
              >
                Search
              </button>
            </form>
          </div>

          {error && (
            <div className="mb-6 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Patient Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : patients.length === 0 ? (
              <div className="p-12 text-center text-slate-500">
                No patients found.
              </div>
            ) : (
              <>
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Patient</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Case #</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Treatment</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Clinic</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Balance</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Lien</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {patients.map((patient) => (
                      <tr key={patient.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-medium text-sm">
                              {patient.first_name?.[0]}{patient.last_name?.[0]}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 text-sm">
                                {patient.first_name} {patient.last_name}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 font-mono">
                          {patient.case_number || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(patient.status)}`}>
                            {patient.status || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {patient.treatment_type || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {patient.treating_clinic || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-slate-900">
                          ${(patient.total_balance || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            {getLienStatusIcon(patient.lien_status)}
                            <span className="text-xs text-slate-500">{patient.lien_status || 'None'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Link
                            to={`/portal/patients/${patient.id}`}
                            className="inline-flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                          >
                            <Eye size={16} />
                            View
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                    <p className="text-sm text-slate-500">
                      Showing {((page - 1) * perPage) + 1} to {Math.min(page * perPage, total)} of {total}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                      >
                        <ChevronLeft size={18} />
                      </button>
                      <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Add Patient Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="add-patient-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-slate-200 sticky top-0 bg-white">
              <h2 className="text-lg font-semibold text-slate-900">Add New Patient</h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleCreatePatient} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={newPatient.first_name}
                    onChange={(e) => setNewPatient({...newPatient, first_name: e.target.value})}
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="First name"
                    data-testid="patient-first-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={newPatient.last_name}
                    onChange={(e) => setNewPatient({...newPatient, last_name: e.target.value})}
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Last name"
                    data-testid="patient-last-name"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    value={newPatient.dob}
                    onChange={(e) => setNewPatient({...newPatient, dob: e.target.value})}
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid="patient-dob"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Case Number
                  </label>
                  <input
                    type="text"
                    value={newPatient.case_number}
                    onChange={(e) => setNewPatient({...newPatient, case_number: e.target.value})}
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Case #"
                    data-testid="patient-case-number"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Attorney
                </label>
                <input
                  type="text"
                  value={newPatient.attorney}
                  onChange={(e) => setNewPatient({...newPatient, attorney: e.target.value})}
                  className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Attorney name"
                  data-testid="patient-attorney"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Treatment Type
                  </label>
                  <input
                    type="text"
                    value={newPatient.treatment_type}
                    onChange={(e) => setNewPatient({...newPatient, treatment_type: e.target.value})}
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="e.g., Physical Therapy"
                    data-testid="patient-treatment"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Insurance
                  </label>
                  <input
                    type="text"
                    value={newPatient.insurance}
                    onChange={(e) => setNewPatient({...newPatient, insurance: e.target.value})}
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Insurance provider"
                    data-testid="patient-insurance"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={newPatient.notes}
                  onChange={(e) => setNewPatient({...newPatient, notes: e.target.value})}
                  className="w-full h-20 px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Additional notes..."
                  data-testid="patient-notes"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 text-sm font-medium hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                  data-testid="submit-patient-btn"
                >
                  {creating ? "Adding..." : "Add Patient"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
